import React, { useContext } from 'react'

import { useCachedData } from 'hooks/useCachedData'
import { usePollApi } from 'hooks/usePollApi'

import {
  Loadable,
  catLoadables,
  mapLoadable,
  mkLoadable,
} from '../utils/loadable'

import { TenantId } from './Tenants'

export type Wallet = {
  id: WalletId
  tenant_id: TenantId
  exchange_id: number
  asset_id: number
  name: string | null
  wallet_type: WalletType
  disabled: boolean
}

type WalletId = number

type WalletMap = Partial<Record<TenantId, { [k: WalletId]: Wallet }>>

export const WalletTypes = [
  'spot',
  'margin',
  'futures',
  'swaps',
  'transfer',
  'lending',
] as const
export type WalletType = (typeof WalletTypes)[number]
export const isWalletType = (str: string): str is WalletType => {
  return WalletTypes.includes(str as WalletType)
}

type WalletsContext = Loadable<{
  wallets: Wallet[]
  walletsByIdByTenant: WalletMap
}>

const WalletsContext = React.createContext<WalletsContext>({ loading: true })

/** Given a list of wallets, group by tenant ID and key each group by wallet ID */
const mkWalletsByIdByTenant = (wallets: Wallet[]): WalletMap => {
  return wallets.reduce<WalletMap>((acc, wallet) => {
    const { tenant_id, id } = wallet
    acc[tenant_id] = acc[tenant_id] ?? {}
    acc[tenant_id][id] = wallet
    return acc
  }, {})
}

export function WalletsProvider({
  children,
}: {
  children: JSX.Element
}): JSX.Element {
  const wallets = useCachedData(
    'contexts:Wallets',
    mkLoadable(usePollApi<Wallet>('hot_wallets', 60000)),
    wallets => wallets.slice(0, 500),
  )

  const walletsByIdByTenant = mapLoadable(wallets, mkWalletsByIdByTenant)

  const value = catLoadables({
    wallets,
    walletsByIdByTenant,
  })

  return (
    <WalletsContext.Provider value={value}>{children}</WalletsContext.Provider>
  )
}

export const useWallet = (
  tenantId: TenantId,
  walletId: WalletId,
): Loadable<Wallet | undefined> =>
  mapLoadable(
    useContext(WalletsContext),
    data => data.walletsByIdByTenant[tenantId]?.[walletId],
  )

export const useWallets = (includeDisabled = false): Loadable<Wallet[]> =>
  mapLoadable(useContext(WalletsContext), data =>
    includeDisabled ? data.wallets : data.wallets.filter(w => !w.disabled),
  )
